import { z } from 'zod';
import { addressSchema, cuidSchema } from './common.dto';
import { OrderStatus, PaymentMethod, PaymentType } from '../enums';

/**
 * Order DTOs. Phase 1: PREPAID only; methods Stripe/PayPal/IBAN.
 * Totals are NOT submitted by the client — Holded computes authoritative totals.
 */

export const orderItemInputSchema = z.object({
  productId: cuidSchema,
  qty: z.number().int().positive(),
  /** Free-of-charge (marketing) line. */
  foc: z.boolean().default(false),
});
export type OrderItemInputDto = z.infer<typeof orderItemInputSchema>;

export const createOrderSchema = z
  .object({
    clientId: cuidSchema,
    /** The billing entity the proforma / invoice is issued to; the client's default when omitted. */
    billingEntityId: cuidSchema.optional(),
    items: z.array(orderItemInputSchema).min(1, 'At least one item is required'),
    /** Office responsible for the order ("Agente responsable") — an Office.code. */
    responsibleOffice: z.string().trim().max(60).optional(),
    /** Phase 1 form exposes PREPAID only; POSTPAY_30D/SEPA kept for the future. */
    paymentType: z.nativeEnum(PaymentType).default(PaymentType.PREPAID),
    paymentMethod: z.nativeEnum(PaymentMethod),
    marketingPack: z.boolean().default(false),
    notes: z.string().trim().max(2000).optional(),
    shippingSameAsBilling: z.boolean().default(true),
    /**
     * Chosen delivery address when shipping differs from billing (picked from the
     * client's managed delivery list, or a one-off entered on the order). Ignored when
     * shippingSameAsBilling is true; falls back to the client's stored shipping address.
     */
    shippingAddress: addressSchema.optional(),
    /**
     * Data-processing consent ("Acepto el tratamiento de la información…").
     * The order form requires it; recorded for audit. Optional server-side so existing
     * programmatic flows/tests are unaffected; the UI blocks submit until checked.
     */
    consentDataProcessing: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    // Phase 1 guard: the order form must submit PREPAID only.
    if (val.paymentType !== PaymentType.PREPAID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paymentType'],
        message: 'Only PREPAID is offered in Phase 1.',
      });
    }
  });
export type CreateOrderDto = z.infer<typeof createOrderSchema>;

/** Admin/super-admin manual status override (records a reason; re-confirms automations). */
export const overrideOrderStatusSchema = z.object({
  status: z.nativeEnum(OrderStatus),
  reason: z.string().trim().min(3).max(1000),
});
export type OverrideOrderStatusDto = z.infer<typeof overrideOrderStatusSchema>;

/** Order list filters (agent sees own; admin sees all). */
export const orderListQuerySchema = z.object({
  status: z.nativeEnum(OrderStatus).optional(),
  clientId: cuidSchema.optional(),
  agentId: cuidSchema.optional(),
  search: z.string().trim().max(120).optional(),
  /** 'true' → only archived orders; default/absent → archived are hidden. */
  archived: z.enum(['true', 'false']).optional(),
});
export type OrderListQueryDto = z.infer<typeof orderListQuerySchema>;
